import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { normalizeText } from '@/utils/normalizeText';
import {
  Search,
  ChevronRight,
  GitFork,
  Video,
  Wand2,
  Share2,
  Mail,
  Shuffle,
  FlaskConical,
  Shield,
  Target,
  BarChart3,
  Radio,
  Activity,
  GitCompareArrows,
  Globe,
  KeyRound,
  Handshake,
  GraduationCap,
  ListVideo,
  Workflow
} from 'lucide-react';

interface ResourceItem {
  icon: React.ElementType;
  label: string;
  category: string;
  to: string;
  keywords: string[];
  /**
   * Ação alternativa à navegação (usada pelas ferramentas que abrem modal).
   * Sem parâmetro de evento de propósito: o resultado é selecionado por teclado
   * também, onde não existe um MouseEvent real para repassar.
   */
  onClick?: () => void;
}

interface SidebarSearchProps {
  onOpenReplic?: () => void;
  onOpenFunnelX?: () => void;
}


export function SidebarSearch({ onOpenReplic, onOpenFunnelX }: SidebarSearchProps) {
  useLanguage();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Índice de recursos — espelha os grupos da Sidebar
  const resources: ResourceItem[] = [
    // VÍDEOS
    { icon: Video, label: translateInterfaceText("Meus Vídeos"), category: 'Vídeos', to: '/dashboard', keywords: ['vídeos', 'dashboard', 'biblioteca'] },
    { icon: Wand2, label: translateInterfaceText("Personalizar"), category: 'Vídeos', to: '/personalizations', keywords: ['customizar', 'editor', 'estilo'] },
    { icon: Share2, label: translateInterfaceText("Compartilhar"), category: 'Vídeos', to: '/shared', keywords: ['distribuir', 'link', 'embed'] },

    // CONVERSÃO
    { icon: Mail, label: translateInterfaceText("Captura de Leads"), category: 'Conversão', to: '/vsleads', keywords: ['leads', 'captura', 'email'] },
    { icon: GitFork, label: translateInterfaceText("VSL Dinâmica"), category: 'Conversão', to: '/vsl-dinamica', keywords: ['vsl', 'dinâmica', 'funil', 'fluxo', 'quiz'] },

    // ANÁLISES
    { icon: BarChart3, label: translateInterfaceText("Métricas"), category: 'Análises', to: '/metrics', keywords: ['dados', 'estatísticas', 'analytics', 'métrica'] },
    { icon: Radio, label: translateInterfaceText("Tempo Real"), category: 'Análises', to: '/tempo-real', keywords: ['ao vivo', 'realtime', 'agora'] },
    { icon: Activity, label: translateInterfaceText("Rastreamento"), category: 'Análises', to: '/tracking', keywords: ['tracking', 'rastrear', 'pixels'] },
    { icon: GitCompareArrows, label: translateInterfaceText("Comparar VSL"), category: 'Análises', to: '/comparar-vsl', keywords: ['comparar', 'vsl', 'split'] },

    // OTIMIZAÇÃO
    { icon: Shuffle, label: translateInterfaceText("Teste A/B"), category: 'Otimização', to: '/ab-test', keywords: ['teste', 'ab', 'split', 'comparação'] },
    { icon: FlaskConical, label: translateInterfaceText("Teste de hook"), category: 'Otimização', to: '/hook-track', keywords: ['hook', 'track', 'hooktrack', 'teste de hook', 'gancho', 'introdução'] },

    // PROTEÇÃO
    { icon: Shield, label: translateInterfaceText("Cloaker"), category: 'Proteção', to: '/vsl-cloaker', keywords: ['cloak', 'proteção', 'filtro'] },
    { icon: Target, label: translateInterfaceText("Pixel"), category: 'Proteção', to: '/pixel', keywords: ['pixel', 'conversão', 'tracking'] },
    { icon: Globe, label: translateInterfaceText("Domínios"), category: 'Proteção', to: '/domain-protection', keywords: ['domínio', 'url', 'site'] },
    { icon: KeyRound, label: translateInterfaceText("Chaves de API"), category: 'Proteção', to: '/api-keys', keywords: ['chave', 'api', 'token', 'integração', 'mcp', 'claude'] },

    // FERRAMENTAS — abrem modal de ativação, não navegam
    { icon: Workflow, label: translateInterfaceText("Criar Sites"), category: 'Ferramentas', to: '#replic', keywords: ['funil', 'automação', 'fluxo', 'site'], onClick: () => onOpenReplic?.() },
    { icon: ListVideo, label: translateInterfaceText("Criar Quiz"), category: 'Ferramentas', to: '#funnelx', keywords: ['lista', 'sequência', 'quiz'], onClick: () => onOpenFunnelX?.() },

    // APRENDIZADO
    { icon: GraduationCap, label: translateInterfaceText("Academy"), category: 'Aprendizado', to: '/academy', keywords: ['curso', 'aprender', 'aulas'] },
    { icon: Handshake, label: translateInterfaceText("Parceiros"), category: 'Aprendizado', to: '/partners', keywords: ['afiliados', 'parceria'] },
  ];

  const searchTerm = normalizeText(query.trim());

  const filteredResources = searchTerm === ''
    ? []
    : resources.filter(item => (
        normalizeText(item.label).includes(searchTerm) ||
        normalizeText(item.category).includes(searchTerm) ||
        item.keywords.some(k => normalizeText(k).includes(searchTerm))
      )).slice(0, 7);

  const handleSelect = (item: ResourceItem) => {
    if (item.onClick) {
      item.onClick();
    } else {
      navigate(item.to);
    }
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredResources.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredResources.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredResources.length) % filteredResources.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(filteredResources[selectedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  return (
    <div className="relative w-full px-3 mb-4" ref={dropdownRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-[#98A2B3]" strokeWidth={2} />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full h-[38px] pl-[38px] pr-3 text-[13px] font-medium bg-[#F8FAFC] border border-[#DDE3EC] rounded-[10px]",
            "placeholder:text-[#98A2B3] placeholder:font-normal",
            "focus:outline-none focus:border-[#BCD0FA] focus:ring-[3px] focus:ring-[#3478F6]/[0.07] transition-all duration-200",
            "text-[#465468]"
          )}
          placeholder={translateInterfaceText("Buscar recurso...")}
        />
      </div>

      {isOpen && query.trim() !== '' && (
        <div className="absolute left-3 right-3 mt-1.5 bg-white border border-[#E7EBF1] rounded-[12px] shadow-[0_12px_24px_-4px_rgba(15,23,42,0.12)] z-[60] overflow-hidden py-1.5">

          {filteredResources.length > 0 ? (
            <div className="flex flex-col">
              {filteredResources.map((item, index) => {
                const Icon = item.icon;
                const isSelected = index === selectedIndex;
                return (
                  <button
                    key={item.to + item.label}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150",
                      isSelected ? "bg-[#F5F8FF]" : "hover:bg-[#F9FAFB]"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-[32px] h-[32px] rounded-lg transition-colors",
                      isSelected ? "bg-[#EEF4FF] text-[#315FD5]" : "bg-[#F8FAFC] text-[#667085]"
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col flex-1 overflow-hidden">
                      <span className={cn(
                        "text-[13px] font-semibold truncate transition-colors",
                        isSelected ? "text-[#315FD5]" : "text-[#1F2937]"
                      )}>
                        {item.label}
                      </span>
                      <span className="text-[11px] text-[#98A2B3] font-normal truncate uppercase tracking-wider">
                        {item.category}
                      </span>
                    </div>
                    <ChevronRight className={cn(
                      "h-3.5 w-3.5 opacity-0 transition-all",
                      isSelected && "opacity-100 translate-x-0.5 text-[#315FD5]"
                    )} />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-5 text-center">
              <p className="text-[13px] font-medium text-[#1F2937]">{translateInterfaceText("Nenhum recurso encontrado")}</p>
              <p className="text-[11px] text-[#98A2B3] mt-0.5">{translateInterfaceText("Tente outro termo.")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
